import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { PlusIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/solid';
import './Survey.css';
import { IDLE_COMPLETION, SUBMIT_NOUNS, SubmitFeedbackAnnouncement, SubmitFeedbackButton } from './SubmitFeedback';

const SurveyEditor = ({ survey, finishActivity, getCompletionContext, backendService, modalService, readOnly = false, completion = IDLE_COMPLETION }) => {
  const [questions, setQuestions] = useState(survey.data.questions || []);
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const submitting = useRef(false);
  // Answer and definition endpoints rewrite the same questions array: serialize
  // every write so the latest intended value persists last.
  const writes = useRef(Promise.resolve());
  // `${questionId}:${field}` → latest unpersisted value of that field.
  const failures = useRef(new Map());
  const scope = useRef({ current: true });
  const owner = backendService?.positionOwner;
  const identity = backendService?.authenticatedIdentity;
  const isScopeCurrent = generation => generation.current && generation === scope.current
    && generation.owner === backendService?.positionOwner
    && generation.identity === backendService?.authenticatedIdentity && !readOnly;
  useLayoutEffect(() => {
    // Writes queued or submits started for another owner/context never continue.
    const generation = { current: true, owner, identity };
    scope.current = generation;
    writes.current = Promise.resolve();
    failures.current = new Map();
    submitting.current = false;
    setIsSubmitting(false);
    setIsCompleting(false);
    setSaveError('');
    setLoading(false);
    return () => { generation.current = false; };
  }, [backendService, owner, identity, readOnly]);

  const enqueue = job => {
    const generation = scope.current;
    const result = writes.current.then(() => (isScopeCurrent(generation) ? job(() => isScopeCurrent(generation)) : undefined));
    writes.current = result.catch(() => {});
    return result;
  };
  const settleWrites = async () => {
    let pending;
    do {
      pending = writes.current;
      await pending;
    } while (pending !== writes.current);
  };
  const showFailures = () => {
    setSaveError([...new Set([...failures.current.values()].map(failure => failure.message))].join(' '));
  };
  const queueStructure = (key, write, message) => enqueue(async isCurrent => {
    try {
      const result = await write();
      if (!isCurrent()) return undefined;
      failures.current.delete(key);
      showFailures();
      return result;
    } catch (error) {
      if (isCurrent()) {
        // A failed addition/removal must be explicitly resolved, not silently
        // retried by Submit (an uncertain addition could otherwise duplicate).
        failures.current.set(key, { retryable: false, message });
        showFailures();
      }
      throw error;
    }
  });
  const queueQuestionUpdate = (questionId, updates) => enqueue(async isCurrent => {
    try {
      await backendService.updateSurveyQuestion(survey._id, questionId, updates);
      if (!isCurrent()) return;
      Object.keys(updates).forEach(field => failures.current.delete(`${questionId}:${field}`));
    } catch (error) {
      if (!isCurrent()) return;
      console.error(error);
      const status = error?.response?.status;
      const retryable = !status || status >= 500;
      const message = retryable
        ? 'Could not save survey changes. Submit to retry.'
        : error?.response?.data?.error || error?.response?.data?.message || 'A survey change was rejected. Change it and try again.';
      Object.entries(updates).forEach(([field, value]) => failures.current.set(`${questionId}:${field}`, { questionId, field, value, retryable, message }));
    }
    showFailures();
  });

  useEffect(() => {
    setQuestions(survey.data.questions || []);
  }, [survey.data.questions]);

  const isTemplate = survey.is_template;
  const isComplete = survey.status === 'complete';
  // Allow editing definition if it's a template OR if it's an ad-hoc survey (no plan affiliation) and not complete.
  // Actually, even plan surveys might need correction?
  // Let's stick to the user request: "as users if I add one in the activity feed myself through the new activity modal"
  // This implies ad-hoc surveys. Ad-hoc surveys have no source.activity_plan_id usually.
  
  const isAdHoc = !survey.source || !survey.source.activity_plan_id;
  const canEditDefinition = !readOnly && (isTemplate || isAdHoc) && !isComplete;

  const handleAddQuestion = async () => {
    if (submitting.current || !isScopeCurrent(scope.current)) return;
    const text = prompt("Enter question text:");
    if (!text) return;
    const generation = scope.current;
    const isCurrent = () => isScopeCurrent(generation);
    try {
      setLoading(true);
      const updatedSurvey = await queueStructure('structure:add', () => backendService.addQuestionToSurvey(survey._id, {
        text,
        type: 'string' // Default to string, user can change later
      }), 'Could not add question. Retry adding it before submitting.');
      if (!isCurrent() || !updatedSurvey) return;
      // Keep newer optimistic local edits; append only the persisted addition.
      setQuestions(prev => [...prev, ...updatedSurvey.data.questions.filter(q => !prev.some(p => p.id === q.id))]);
    } catch (err) {
      if (!isCurrent()) return;
      console.error(err);
      alert('Failed to add question');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const handleRemoveQuestion = async (questionId) => {
    if (submitting.current || !isScopeCurrent(scope.current) || !window.confirm("Remove this question?")) return;
    const generation = scope.current;
    const isCurrent = () => isScopeCurrent(generation);
    try {
      setLoading(true);
      const updatedSurvey = await queueStructure(`structure:remove:${questionId}`,
        () => backendService.removeQuestionFromSurvey(survey._id, questionId),
        'Could not remove question. Retry removing it before submitting.');
      if (!isCurrent() || !updatedSurvey) return;
      setQuestions(prev => prev.filter(q => q.id !== questionId));
    } catch (err) {
      if (!isCurrent()) return;
      console.error(err);
      alert('Failed to remove question');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const handleUpdateQuestionDefinition = (questionId, updates) => {
    if (submitting.current) return;
    // Optimistic update
    setQuestions(prev => prev.map(q => q.id === questionId ? { ...q, ...updates } : q));
    queueQuestionUpdate(questionId, updates);
  };

  const handleAnswerChange = (questionId, newValue) => {
    if (readOnly || submitting.current) return;
    // Optimistic update
    setQuestions(prev => prev.map(q => q.id === questionId ? { ...q, answer: newValue } : q));
    queueQuestionUpdate(questionId, { answer: newValue });
  };

  const handleSetDefault = (questionId, newValue) => {
    if (submitting.current) return;
    // For templates, the "answer" UI sets the defaultValue
    setQuestions(prev => prev.map(q => q.id === questionId ? { ...q, defaultValue: newValue, answer: newValue } : q));
    queueQuestionUpdate(questionId, { defaultValue: newValue, answer: newValue });
  };

  const handleSubmit = async () => {
    if (submitting.current || readOnly || isTemplate || isComplete) return;
    const generation = scope.current;
    const initiatingContext = getCompletionContext?.();
    const isCurrent = () => isScopeCurrent(generation) && !initiatingContext?.signal?.aborted;
    submitting.current = true;
    setIsSubmitting(true);
    try {
      // Inputs are frozen now, so this drains every write the user intended.
      await settleWrites();
      if (!isCurrent()) return;
      // Retry only settled retryable failures; each holds its field's latest value.
      const retries = new Map();
      failures.current.forEach(({ questionId, field, value, retryable }) => {
        if (retryable) retries.set(questionId, { ...retries.get(questionId), [field]: value });
      });
      if (retries.size) {
        retries.forEach((updates, questionId) => queueQuestionUpdate(questionId, updates));
        await settleWrites();
        if (!isCurrent()) return;
      }
      showFailures();
      if (failures.current.size) return;
      setIsCompleting(true);
      if (initiatingContext) await finishActivity(survey._id, { initiatingContext });
      else await finishActivity(survey._id);
    } finally {
      if (isScopeCurrent(generation)) {
        submitting.current = false;
        setIsSubmitting(false);
        setIsCompleting(false);
      }
    }
  };
  // Draining local writes is part of saving; the parent then owns completion stages.
  const feedback = completion.pending || completion.saved || !isSubmitting || isCompleting
    ? completion : { pending: true, stage: 'saving', saved: false };

  const renderQuestionInput = (q) => {
    const value = isTemplate ? q.defaultValue : q.answer;
    const handleChange = (val) => isTemplate ? handleSetDefault(q.id, val) : handleAnswerChange(q.id, val);

    if (readOnly) {
      const answer = value == null || value === '' ? 'Not answered' : q.type === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
      return <div className="survey-readonly-answer">{answer}</div>;
    }

    if (q.type === 'boolean') {
      return (
        <div className="survey-boolean-group">
            <button 
                className={`btn-choice ${value === true ? 'selected' : ''}`} 
                onClick={() => handleChange(true)}
                disabled={isComplete || isSubmitting}
            >Yes</button>
            <button 
                className={`btn-choice ${value === false ? 'selected' : ''}`} 
                onClick={() => handleChange(false)}
                 disabled={isComplete || isSubmitting}
            >No</button>
        </div>
      );
    } else if (q.type === 'choice') {
        return (
            <div className="survey-choice-group">
                 {/*  If template, allow adding options? */}
                 {q.options && q.options.map((opt, idx) => (
                      <button 
                        key={idx}
                        className={`btn-choice ${value === opt ? 'selected' : ''}`} 
                        onClick={() => handleChange(opt)}
                         disabled={isComplete || isSubmitting}
                    >{opt}</button>
                 ))}
                 {canEditDefinition && (
                     <button className="btn-add-choice" disabled={isSubmitting} onClick={() => {
                         const newOpt = prompt("New option:");
                         if(newOpt) {
                             const newOptions = [...(q.options || []), newOpt];
                             handleUpdateQuestionDefinition(q.id, { options: newOptions });
                         }
                     }}>+</button>
                 )}
            </div>
        )
    } else {
        return (
            <textarea 
                className="survey-text-input"
                value={value || ''}
                onChange={(e) => handleChange(e.target.value)}
                disabled={readOnly || isComplete || isSubmitting}
                placeholder={isTemplate ? "Default Answer..." : "Type your answer..."}
            />
        )
    }
  };

  return (
    <div className="survey-container">
      {questions.map((q, index) => (
        <div key={q.id} className="survey-question-card">
          <div className="survey-question-header">
            {canEditDefinition ? (
                <input 
                    className="survey-question-title-edit"
                    value={q.text}
                    disabled={isSubmitting}
                    onChange={(e) => handleUpdateQuestionDefinition(q.id, { text: e.target.value })}
                />
            ) : (
                <div className="survey-question-text">{q.text}</div>
            )}
            
            {canEditDefinition && (
                <div className="survey-question-controls">
                     <select 
                        value={q.type} 
                        onChange={(e) => handleUpdateQuestionDefinition(q.id, { type: e.target.value })}
                        disabled={isSubmitting}
                        className="survey-type-select"
                    >
                         <option value="string">Text</option>
                         <option value="boolean">Yes/No</option>
                         <option value="choice">Choice</option>
                     </select>
                    <button className="btn-icon-danger" disabled={loading || isSubmitting} onClick={() => handleRemoveQuestion(q.id)}>
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </div>
            )}
          </div>
          
          <div className="survey-question-body">
              {renderQuestionInput(q)}
          </div>
        </div>
      ))}

      {canEditDefinition && (
        <button className="btn-add-question" onClick={handleAddQuestion} disabled={loading || isSubmitting}>
          <PlusIcon className="h-5 w-5 inline-block mr-1" /> Add Question
        </button>
      )}

      {!readOnly && !isTemplate && (!isComplete || completion.pending) && (
        <div className="survey-actions">
           {/* Usually "Finish" is handled by Activity wrapper, but we need to trigger it. 
               The wrapper calls finishActivity. */}
           <SubmitFeedbackButton
              className="btn-complete-survey"
              label="Submit Survey"
              icon={<CheckIcon />}
              feedback={feedback}
              noun={SUBMIT_NOUNS.survey}
              onClick={handleSubmit}
              disabled={isSubmitting || questions.some(q => !q.answer && q.answer !== false)}
              style={questions.some(q => !q.answer && q.answer !== false) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              title={questions.some(q => !q.answer && q.answer !== false) ? "Please answer all questions" : ""}
           />
        </div>
      )}
      {saveError && <p role="alert">{saveError}</p>}
      <SubmitFeedbackAnnouncement feedback={feedback} noun={SUBMIT_NOUNS.survey} />
    </div>
  );
};

// A different survey owns a different write queue and completion lifecycle.
const Survey = props => <SurveyEditor key={props.survey._id} {...props} />;
export default Survey;
